<script setup lang="ts">
import gsap from 'gsap'

import type { FactListRecord } from '~/types/datocms'

defineProps<{
  data: FactListRecord | SectionFactListRecord
}>()

const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null
let tl: GSAPTimeline | null = null

onMounted(() => {
  if (ctxRef.value) {
    ctx = gsap.context(() => {
      // Animated Frames
      gsap.set('.fx-frame-l', {
        x: '4vw',
        clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)'
      })
      gsap.set('.fx-frame-r', { x: '-4vw', clipPath: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)' })
      gsap.set('.fx-frame-y', { clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)' })

      tl = gsap.timeline({
        scrollTrigger: {
          trigger: ctxRef.value,
          start: 'center bottom'
        }
      })
      tl.addLabel('start')
      tl.to(
        '.fx-frame-l, .fx-frame-r',
        {
          duration: 1.5,
          x: '0vw',
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
          ease: 'power4.out'
        },
        'start'
      )
      tl.to(
        '.fx-frame-y',
        {
          duration: 1.5,
          clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
          ease: 'power4.out'
        },
        'start'
      )
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="relative my-12 py-[10.5rem] lg:my-20 lg:py-0"
    :class="{
      'mx-auto lg:w-full lg:max-w-editor-media': data.facts.length === 1
    }"
  >
    <svg
      class="fx-frame-y absolute left-1/2 top-0 -translate-x-1/2 mix-blend-difference invert lg:hidden"
      width="203"
      height="169"
      viewBox="0 0 203 169"
      fill="none"
    >
      <path
        d="M149.463 116L179.059 116C191.729 116 202 126.271 202 138.941L202 168.537"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path
        d="M0.999868 168.537L0.999869 138.941C0.99987 126.271 11.2711 116 23.9408 116L53.5303 116"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path d="M101.5 0L101.5 116.5" stroke="black" stroke-width="2" />
    </svg>
    <svg
      class="fx-frame-y absolute bottom-0 left-1/2 -translate-x-1/2 mix-blend-difference invert lg:hidden"
      width="203"
      height="170"
      viewBox="0 0 203 170"
      fill="none"
    >
      <path
        d="M53.5366 52.5361L23.9409 52.5361C11.2712 52.5361 1 42.2649 0.999999 29.5953L0.999998 -0.000477285"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path
        d="M202 -0.000479888L202 29.5952C202 42.2649 191.729 52.5361 179.059 52.5361L149.47 52.5361"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path d="M102 169.5L102 53" stroke="black" stroke-width="2" />
    </svg>
    <svg
      class="fx-frame-l absolute left-1/2 top-0 hidden origin-center -translate-x-1/2 rotate-90 mix-blend-difference invert lg:left-0 lg:block lg:translate-x-0 lg:rotate-0"
      width="135"
      height="203"
      viewBox="0 0 135 203"
      fill="none"
    >
      <path
        d="M82 53.5366V23.9409C82 11.2712 92.2712 1 104.941 1H134.537"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path
        d="M134.537 202H104.941C92.2712 202 82 191.729 82 179.059V149.47"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path d="M-34 101.5L82.5 101.5" stroke="black" stroke-width="2" />
    </svg>
    <div
      class="mx-auto flex w-fit flex-col items-center gap-8 py-4 lg:h-[12.688rem] lg:flex-row lg:gap-16 lg:px-10 lg:py-0 xl:gap-24"
    >
      <div v-for="fact in data.facts" :key="fact.id" class="flex flex-col">
        <div v-if="fact.value" class="text-6xl-m lg:text-8xl-m">
          <TextReveal :delay="0.4">{{ fact.value }}</TextReveal>
        </div>
        <div v-if="fact.text" class="text-center text-lg opacity-40 lg:text-left">
          <TextReveal>{{ fact.text }}</TextReveal>
        </div>
      </div>
    </div>
    <svg
      class="fx-frame-r absolute bottom-0 left-1/2 hidden -translate-x-1/2 rotate-90 mix-blend-difference invert lg:left-auto lg:right-0 lg:block lg:translate-x-0 lg:-rotate-0"
      width="135"
      height="203"
      viewBox="0 0 135 203"
      fill="none"
    >
      <path
        d="M52.5371 149.463L52.5371 179.059C52.5371 191.729 42.2659 202 29.5962 202L0.000499277 202"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path
        d="M0.000503563 0.999866L29.5962 0.999868C42.2659 0.999869 52.5371 11.2711 52.5371 23.9408L52.5371 53.5303"
        stroke="#1D1D1B"
        stroke-width="2"
        stroke-miterlimit="10"
      />
      <path d="M168.537 101.5L52.0371 101.5" stroke="black" stroke-width="2" />
    </svg>
  </div>
</template>
